// Checkpoint + resume for an in-flight tailoring run. Chrome's Memory Saver
// can DISCARD a background tab mid-run (common while screensharing); when the
// user returns, the page reloads from scratch and the run's in-memory state is
// gone. sessionStorage survives that reload, so every completed stage call is
// checkpointed here: on resume, finished stages replay instantly (no re-bill,
// no re-wait) and the timer continues from the ORIGINAL start timestamp.

const RUN_KEY = 'tw-active-run';
const CACHE_PREFIX = 'tw-rc:';
const MAX_RUN_AGE_MS = 45 * 60_000;

export interface ActiveRun { runId: string; startTs: number }

function djb2(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

export function getActiveRun(): ActiveRun | null {
  try {
    const raw = sessionStorage.getItem(RUN_KEY);
    if (!raw) return null;
    const run = JSON.parse(raw) as ActiveRun;
    if (!run?.runId || !run?.startTs || Date.now() - run.startTs > MAX_RUN_AGE_MS) return null;
    return run;
  } catch { return null; }
}

/** Start a run, RESUMING the interrupted one when a fresh checkpoint exists. */
export function beginOrResumeRun(): { run: ActiveRun; resumed: boolean } {
  const existing = getActiveRun();
  if (existing) return { run: existing, resumed: true };
  const run: ActiveRun = { runId: Math.random().toString(36).slice(2, 10), startTs: Date.now() };
  try { sessionStorage.setItem(RUN_KEY, JSON.stringify(run)); } catch { /* quota/private mode: run just is not resumable */ }
  return { run, resumed: false };
}

/** The run finished (or the user cancelled): drop the record and every checkpoint. */
export function endRun(): void {
  try {
    sessionStorage.removeItem(RUN_KEY);
    const stale: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(CACHE_PREFIX)) stale.push(k);
    }
    for (const k of stale) sessionStorage.removeItem(k);
  } catch { /* nothing to clean */ }
}

function cacheKey(runId: string, label: string, body: unknown): string {
  return `${CACHE_PREFIX}${runId}:${label}:${djb2(JSON.stringify(body))}`;
}

export function checkpointGet(runId: string, label: string, body: unknown): unknown | null {
  try {
    const raw = sessionStorage.getItem(cacheKey(runId, label, body));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function checkpointSet(runId: string, label: string, body: unknown, result: unknown): void {
  try { sessionStorage.setItem(cacheKey(runId, label, body), JSON.stringify(result)); } catch { /* quota: stage will re-run on resume */ }
}
